import { useState, useEffect, useCallback } from 'react'
import { Table, Card, Button, Space, Tag, message, Input, Modal, Form, Select, InputNumber } from 'antd'
import { SearchOutlined, ReloadOutlined, PlusOutlined, LinkOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'

interface SmartTrayPlate {
  id: number
  card_no: string
  prefix: string
  serial: number | null
  qrcode: string | null
  franchiser_id: number | null
  restaurant_id: number | null
  status: number
  created_at: string
}

interface SmartTrayUser {
  id: number
  other_uid: string
  phone: string
  nickname: string
}

interface BindResult {
  order_no: string
  user_id: number
  phone: string
  balance: number
}

export default function SmartTrayPlates() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<SmartTrayPlate[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [keyword, setKeyword] = useState('')
  const [addModalVisible, setAddModalVisible] = useState(false)
  const [addForm] = Form.useForm()
  const [addLoading, setAddLoading] = useState(false)
  const [bindModalVisible, setBindModalVisible] = useState(false)
  const [bindForm] = Form.useForm()
  const [bindLoading, setBindLoading] = useState(false)
  const [users, setUsers] = useState<SmartTrayUser[]>([])
  const [userLoading, setUserLoading] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`/api/smart-tray/plates?page=${page}&page_size=${pageSize}&keyword=${keyword}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }).then(r => r.json())
      if (res.code === 0 || res.code === 200) {
        setData(res.data?.list || [])
        setTotal(Number(res.data?.total) || 0)
      }
    } catch {
      message.error('加载餐盘列表失败')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, keyword])

  useEffect(() => { loadData() }, [loadData])

  const loadUsers = async () => {
    setUserLoading(true)
    try {
      const res = await fetch('/api/smart-tray/users?page=1&page_size=200', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }).then(r => r.json())
      if (res.code === 0 || res.code === 200) {
        setUsers(res.data?.list || [])
      }
    } catch {
      message.error('加载用户列表失败')
    } finally {
      setUserLoading(false)
    }
  }

  const handleAdd = async () => {
    try {
      const values = await addForm.validateFields()
      setAddLoading(true)
      const res = await fetch('/api/smart-tray/plates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(values)
      }).then(r => r.json())
      if (res.code === 0 || res.code === 200) {
        message.success('添加餐盘成功')
        setAddModalVisible(false)
        addForm.resetFields()
        loadData()
      } else {
        message.error(res.message || '添加失败')
      }
    } catch {
    } finally {
      setAddLoading(false)
    }
  }

  const handleOpenBind = () => {
    loadUsers()
    setBindModalVisible(true)
  }

  const handleBind = async () => {
    try {
      const values = await bindForm.validateFields()
      setBindLoading(true)
      const res = await fetch('/api/smart-tray/plates/bind', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(values)
      }).then(r => r.json())
      if (res.code === 0 || res.code === 200) {
        const d: BindResult = res.data
        message.success(`绑盘成功！订单号: ${d.order_no}，余额: ¥${d.balance}`)
        setBindModalVisible(false)
        bindForm.resetFields()
        loadData()
      } else {
        message.error(res.message || '绑盘失败: ' + (res.detail?.msg || ''))
      }
    } catch {
    } finally {
      setBindLoading(false)
    }
  }

  const columns: ColumnsType<SmartTrayPlate> = [
    { title: '餐盘编号', dataIndex: 'card_no', width: 200 },
    { title: '前缀', dataIndex: 'prefix', width: 100, render: v => v || '-' },
    { title: '二维码', dataIndex: 'qrcode', width: 200, render: v => v || '-' },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v: number) => v === 1 ? <Tag color="green">正常</Tag> : <Tag color="red">停用</Tag>
    },
    { title: '加盟商ID', dataIndex: 'franchiser_id', width: 100, render: v => v || '-' },
    { title: '餐厅ID', dataIndex: 'restaurant_id', width: 100, render: v => v || '-' },
    { title: '创建时间', dataIndex: 'created_at', width: 180 }
  ]

  return (
    <Card
      title="智慧餐盘管理"
      extra={
        <Space>
          <Input
            placeholder="搜索餐盘编号"
            prefix={<SearchOutlined />}
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onPressEnter={() => { setPage(1); loadData() }}
            style={{ width: 200 }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>
            添加餐盘
          </Button>
          <Button icon={<LinkOutlined />} onClick={handleOpenBind}>
            主动绑定
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
        </Space>
      }
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, ps) => { setPage(p); setPageSize(ps) }
        }}
      />

      {/* 添加餐盘弹窗 */}
      <Modal
        title="添加餐盘"
        open={addModalVisible}
        onOk={handleAdd}
        onCancel={() => setAddModalVisible(false)}
        confirmLoading={addLoading}
      >
        <Form form={addForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="card_no" label="餐盘编号" rules={[{ required: true, message: '请输入餐盘编号' }]}>
            <Input placeholder="例如: PLATE-001" />
          </Form.Item>
          <Form.Item name="prefix" label="前缀">
            <Input placeholder="可选" />
          </Form.Item>
          <Form.Item name="serial" label="序号">
            <InputNumber style={{ width: '100%' }} placeholder="可选，数字" />
          </Form.Item>
          <Form.Item name="qrcode" label="二维码">
            <Input placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 主动绑定弹窗 */}
      <Modal
        title="主动绑定 - 推送绑盘信息到取餐机"
        open={bindModalVisible}
        onOk={handleBind}
        onCancel={() => setBindModalVisible(false)}
        confirmLoading={bindLoading}
        width={480}
      >
        <div style={{ background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 4, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          选择餐盘和用户后，系统将主动调用安鼎接口把绑定关系推送给取餐机。取餐机显示"已绑盘"后方可使用。
        </div>
        <Form form={bindForm} layout="vertical">
          <Form.Item name="card_no" label="餐盘编号" rules={[{ required: true, message: '请输入餐盘编号' }]}>
            <Input placeholder="例如: PLATE-001" />
          </Form.Item>
          <Form.Item
            name="user_id"
            label="绑定用户"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Select
              showSearch
              placeholder="搜索用户手机号/昵称"
              loading={userLoading}
              filterOption={(input, option) =>
                (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
              }
              options={users.map(u => ({
                value: u.id,
                label: `${u.phone} ${u.nickname ? '(' + u.nickname + ')' : ''} [${u.other_uid}]`
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  )
}
